import { Outlet, Link } from "react-router-dom";
import { Stars, ShieldCheck, Wind, ArrowUpRight } from "lucide-react";
import { VertaliLogo } from "@/components/logo";

/**
 * Split-screen auth shell.
 *  - Left panel: brand experience with animated atmosphere, logo, marketing.
 *  - Right panel: scrollable form area (Outlet renders the actual page).
 *  - Mobile: brand panel collapses into a slim header strip above the form.
 */
export function AuthLayout() {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background text-foreground">
      {/* ═══ Brand panel ═══ */}
      <aside
        className="relative overflow-hidden lg:w-[44%] xl:w-[40%] lg:min-h-screen
                   text-white flex flex-col justify-between p-6 sm:p-10 lg:p-12"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.07 0.05 270) 0%, oklch(0.18 0.10 250) 50%, oklch(0.07 0.05 270) 100%)",
        }}
      >
        {/* Atmosphere */}
        <div className="absolute inset-0 atmospheric-grid opacity-30 pointer-events-none" />
        <div
          className="absolute -top-40 -right-32 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-30 glow-pulse pointer-events-none"
          style={{ background: "oklch(0.65 0.22 230)" }}
        />
        <div
          className="absolute -bottom-44 -left-28 w-[22rem] h-[22rem] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: "oklch(0.6 0.22 280)" }}
        />
        {/* Soft star field for the night-sky vibe */}
        <Starfield />

        {/* Logo + brand row */}
        <div className="relative z-10 flex items-start justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <VertaliLogo
              size={28}
              className="text-white drop-shadow-[0_0_24px_rgba(99,179,237,0.35)]"
              secondaryOpacity={0.65}
            />
          </Link>
          <Link
            to="/"
            className="hidden lg:inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em]
                       text-white/70 hover:text-white transition-colors rounded-full border border-white/15 px-3 py-1.5 backdrop-blur-sm"
          >
            Live forecast
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Marketing block */}
        <div className="relative z-10 hidden lg:block max-w-xl">
          <p className="text-[11px] uppercase tracking-[0.3em] text-primary/90 font-bold mb-4">
            Vertali Weather
          </p>
          <h2 className="text-3xl xl:text-4xl 2xl:text-5xl font-black leading-[1.05] tracking-tight">
            The forecast,
            <br />
            <span className="bg-gradient-to-r from-sky-200 via-indigo-200 to-violet-200 bg-clip-text text-transparent">
              decoded with science.
            </span>
          </h2>
          <p className="text-sm text-white/75 leading-relaxed mt-5 max-w-md">
            Real meteorological data. A spoken briefing that talks you through your day.
            Astronomy and air quality computed at your exact coordinates. None of your data
            is sold.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-3 max-w-sm">
            <Feature
              icon={<Wind className="h-4 w-4 text-sky-300" />}
              title="Hyper-local insight"
              text="Sun position, twilight phases, comfort and driving safety scored on the spot."
            />
            <Feature
              icon={<Stars className="h-4 w-4 text-violet-300" />}
              title="A sky you can see"
              text="Live moon phases, planet visibility, and meteor-shower windows for tonight."
            />
            <Feature
              icon={<ShieldCheck className="h-4 w-4 text-emerald-300" />}
              title="Private by design"
              text="No advertising trackers. No corporate voice assistant. Just the weather."
            />
          </div>
        </div>

        {/* Footer line */}
        <div className="relative z-10 flex items-center justify-between gap-4 text-[11px] text-white/55">
          <span>© Vertali</span>
          <span className="hidden sm:inline">Made with public meteorological data.</span>
        </div>
      </aside>

      {/* ═══ Form panel ═══ */}
      <main
        role="main"
        className="flex-1 flex items-center justify-center p-5 sm:p-8 lg:p-12 relative"
      >
        {/* Subtle decorative glow on form side too */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
          style={{ background: "oklch(0.7 0.18 230)" }}
        />
        <div className="relative w-full max-w-md">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Feature({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3 backdrop-blur-sm">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15 flex-shrink-0">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold tracking-tight text-white">{title}</p>
        <p className="text-[11px] text-white/65 leading-snug mt-0.5">{text}</p>
      </div>
    </div>
  );
}

/**
 * Deterministic star field rendered as positioned divs (no canvas overhead).
 */
function Starfield() {
  const stars: { x: number; y: number; size: number; opacity: number; delay: number }[] = [];
  let seed = 4242;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
  for (let i = 0; i < 60; i++) {
    stars.push({
      x: rand() * 100,
      y: rand() * 100,
      size: rand() * 1.6 + 0.5,
      opacity: rand() * 0.65 + 0.2,
      delay: rand() * 4,
    });
  }
  return (
    <div className="absolute inset-0 pointer-events-none">
      {stars.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white glow-pulse"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            opacity: s.opacity,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
